import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'shared/Button';
import DoubleBadge from 'shared/DoubleBadge';
import { NavigationCompany } from 'types/Navigation';
import { PaginatedList } from 'types/PaginatedList';
import { useGet } from 'utils/hooks/useAPI';
import { useSiteSelectorContext } from '../SiteSelectorContext';
import { SiteGroup } from './SiteGroup';
import { SiteList } from './SiteList';
import { SiteSelectorNotice } from './SiteSelectorNotice';
import { Spinner } from 'shared/Spinner';

const NAVIGATION_COMPANIES_API = '/helios-api/navigation/companies';

export const CompanyList = () => {
    const { t } = useTranslation('navigation', {
        keyPrefix: 'site_selector.lists.company_list',
    });

    const { urlSearchParams } = useSiteSelectorContext();

    const [companies, setCompanies] = useState(
        new Map<number, NavigationCompany>()
    );

    const initialUrl = `${NAVIGATION_COMPANIES_API}?${new URLSearchParams([
        ['page', '1'],
        ...urlSearchParams,
    ])}`;

    const previousInitialUrlRef = useRef(initialUrl);

    const [nextPageUrl, setNextPageUrl] = useState(initialUrl);

    const [{ data, error, loading }] =
        useGet<PaginatedList<NavigationCompany>>(nextPageUrl);

    const onClickLoadMore = useCallback(() => {
        if (data?.links?.next && !loading) {
            setNextPageUrl(data.links.next);
        }
    }, [data, loading]);

    useEffect(() => {
        if (data?.results) {
            setCompanies((currentCompanies) => {
                const nextCompanies = new Map(currentCompanies);

                data.results.forEach((result) =>
                    nextCompanies.set(result.company_id, result)
                );

                return nextCompanies;
            });
        }
    }, [data]);

    useEffect(() => {
        if (initialUrl !== previousInitialUrlRef.current) {
            setCompanies(new Map());
            setNextPageUrl(initialUrl);
        }

        previousInitialUrlRef.current = initialUrl;
    }, [initialUrl]);

    if (!loading && error)
        return <SiteSelectorNotice message="service-error" />;

    if (!loading && !companies.size)
        return <SiteSelectorNotice message="no-sites" />;

    if (loading && !error && !companies.size)
        return (
            <div className="w-full h-full flex flex-col items-center justify-start">
                <Spinner />
            </div>
        );

    return (
        <>
            {[...companies.values()].map(
                ({ company_id, name, sites, sites_count }) => (
                    <SiteGroup
                        key={company_id}
                        id={company_id}
                        sites_count={sites_count}
                        title={
                            <span className="flex items-start">
                                <span className="flex-1">{name}</span>
                                <DoubleBadge color="teal">
                                    {company_id}
                                </DoubleBadge>
                            </span>
                        }
                    >
                        <SiteList initialSites={sites} />
                    </SiteGroup>
                )
            )}

            {!error && data?.links?.next ? (
                <Button
                    loading={loading}
                    size="xs"
                    type="button"
                    onClick={onClickLoadMore}
                >
                    {t('more', { count: data.count - companies.size })}
                </Button>
            ) : null}
        </>
    );
};
